import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Autocomplete,
  TextField,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import {
  PRIMARY_BUTTON_SX,
  SECONDARY_BUTTON_SX,
} from "../../theme/buttonstyles";
import ReportPreview from "../reports/ReportPreview";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BarChartIcon from "@mui/icons-material/BarChart";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArticleIcon from "@mui/icons-material/Article";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

import api, { isCancel } from "../../services/api/axios";
import { useAuth } from "../../providers/AuthProvider";
import useModules from "../../hooks/useModules";
import { useNavigate } from "react-router-dom";
import {
  BRAND_MODAL_PAPER_PROPS,
  BRAND_MODAL_TITLE_SX,
} from "../../shared/components/modalStyles";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const PAGE_BG = "#f7f9fc";

const MONTHS = [
  { label: "January", value: 0 },
  { label: "February", value: 1 },
  { label: "March", value: 2 },
  { label: "April", value: 3 },
  { label: "May", value: 4 },
  { label: "June", value: 5 },
  { label: "July", value: 6 },
  { label: "August", value: 7 },
  { label: "September", value: 8 },
  { label: "October", value: 9 },
  { label: "November", value: 10 },
  { label: "December", value: 11 },
];

const toFirstDay = (year, monthIndex) => new Date(year, monthIndex, 1);
const toLastDay = (year, monthIndex) => new Date(year, monthIndex + 1, 0);

const isValidDate = (d) => d instanceof Date && !isNaN(d);

export default function Metrics() {
  const { token } = useAuth();
  const { hasComponent } = useModules();
  const navigate = useNavigate();

  const today = useMemo(() => new Date(), []);

  // Default: year to date
  const defaultStartDate = useMemo(
    () => new Date(today.getFullYear(), 0, 1),
    [today],
  );
  const defaultEndDate = useMemo(() => today, [today]);

  // "Committed" range used by API
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);

  // Draft month/year selectors (drive UI)
  const [draftStartMonth, setDraftStartMonth] = useState(
    MONTHS.find((m) => m.value === defaultStartDate.getMonth()) || MONTHS[0],
  );
  const [draftStartYear, setDraftStartYear] = useState(
    String(defaultStartDate.getFullYear()),
  );
  const [draftEndMonth, setDraftEndMonth] = useState(
    MONTHS.find((m) => m.value === defaultEndDate.getMonth()) || MONTHS[0],
  );
  const [draftEndYear, setDraftEndYear] = useState(
    String(defaultEndDate.getFullYear()),
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metricsError, setMetricsError] = useState("");

  const [aiOpen, setAiOpen] = useState(false);
  const [aiData, setAiData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [aiTab, setAiTab] = useState(0);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [activeView, setActiveView] = useState("overview");
  const [activePreset, setActivePreset] = useState("ytd");
  const [customOpen, setCustomOpen] = useState(false);

  const fmtISO = (d) =>
    isValidDate(d) ? d.toISOString().slice(0, 10) : undefined;

  // Year options (simple: current year +/- 8)
  const yearOptions = useMemo(() => {
    const y = today.getFullYear();
    const arr = [];
    for (let i = y + 1; i >= y - 8; i--) arr.push(String(i));
    return arr;
  }, [today]);

  // Apply draft -> committed (and keep range sane)
  const commitRange = (
    nextStartMonth,
    nextStartYear,
    nextEndMonth,
    nextEndYear,
  ) => {
    const sy = Number(nextStartYear);
    const ey = Number(nextEndYear);
    const sm = nextStartMonth?.value ?? 0;
    const em = nextEndMonth?.value ?? 0;

    const nextStart = toFirstDay(sy, sm);
    const nextEnd = toLastDay(ey, em);

    if (nextStart > nextEnd) {
      // If invalid, force end to same month/year as start
      const forcedEnd = toLastDay(sy, sm);
      setEndDate(forcedEnd);
      setDraftEndYear(String(sy));
      setDraftEndMonth(MONTHS.find((m) => m.value === sm) || MONTHS[0]);
      setStartDate(nextStart);
      return;
    }

    setStartDate(nextStart);
    setEndDate(nextEnd);
  };

  /* ===============================
     FETCH METRICS (V2)
  =============================== */
  useEffect(() => {
    if (!token) return;

    const start = fmtISO(startDate);
    const end = fmtISO(endDate);
    if (!start || !end) return;

    const ctrl = new AbortController();
    setLoading(true);
    setMetricsError("");

    api
      .get("/metrics/v2", {
        signal: ctrl.signal,
        params: {
          start,
          end,
          compare_to: "previous_period",
        },
      })
      .then((res) => setData(res.data))
      .catch((err) => {
        if (!isCancel(err)) {
          setMetricsError(
            err?.response?.data?.detail ||
              err?.message ||
              "Failed to load metrics.",
          );
        }
      })
      .finally(() => setLoading(false));

    return () => ctrl.abort();
  }, [startDate, endDate, token]);

  /* ===============================
     AI SUMMARY
  =============================== */
  const fmtPeriod = (p) => {
    if (!p || p.length < 7) return p;
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const m = parseInt(p.slice(5, 7), 10) - 1;
    const y = p.slice(2, 4);
    return `${monthNames[m] || p} '${y}`;
  };

  const fmtProjectionValue = (metric, val) => {
    if (val == null || val === undefined) return "";
    if (metric === "investment") {
      if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
      if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
      return `$${Math.round(val).toLocaleString()}`;
    }
    if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
    return `${Math.round(val)}`;
  };

  const flattenAiData = (d) => {
    if (!d) return "";
    const out = [];
    if (d.highlights?.length) {
      out.push("KEY HIGHLIGHTS\n" + "─".repeat(40));
      d.highlights.forEach((h) =>
        out.push(`${h.label}: ${h.value}${h.context ? `\n  ${h.context}` : ""}`)
      );
      out.push("");
    }
    d.sections?.forEach((s) => {
      out.push(
        `${s.title.toUpperCase()}${s.key_stat ? ` (${s.key_stat})` : ""}\n` + "─".repeat(40)
      );
      out.push(s.summary);
      out.push("");
    });
    if (d.projections?.length) {
      out.push("3-MONTH PROJECTIONS\n" + "─".repeat(40));
      d.projections.forEach((p) => {
        if (p.reasoning)
          out.push(`${p.display_name}: ${p.reasoning} [Confidence: ${p.confidence}]`);
      });
      out.push("");
    }
    if (d.alerts?.length) {
      out.push("ALERTS & CONCERNS\n" + "─".repeat(40));
      d.alerts.forEach((a) => out.push(`[${a.severity.toUpperCase()}] ${a.message}`));
    }
    return out.join("\n");
  };

  const generateSummary = async () => {
    if (!data?.current?.time_series?.length) return;
    setSummaryLoading(true);
    try {
      const res = await api.post("/gpt/metrics-summary", data.current.time_series);
      setAiData(res?.data || null);
      setAiTab(0);
    } catch (err) {
      setAiData({ _error: err?.response?.data?.detail || "Failed to generate analysis." });
    } finally {
      setSummaryLoading(false);
    }
  };

  const copySummary = async () => {
    await navigator.clipboard.writeText(flattenAiData(aiData));
    setToastSeverity("success");
    setToastMsg("Copied to clipboard");
    setToastOpen(true);
  };

  const downloadSummary = () => {
    const blob = new Blob([flattenAiData(aiData)], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `metrics_insights_${fmtISO(startDate)}_${fmtISO(endDate)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatRangeLabel = (start, end) => {
    if (!isValidDate(start) || !isValidDate(end)) return "Select a range";
    const startLabel = start.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
    const sameYear = start.getFullYear() === end.getFullYear();
    const endLabel = end.toLocaleDateString("en-US", {
      month: "short",
      year: sameYear ? undefined : "numeric",
    });
    return `${startLabel} - ${endLabel}${sameYear ? ` ${end.getFullYear()}` : ""}`;
  };

  const rangeLabel = formatRangeLabel(startDate, endDate);

  const setRange = (preset, start, end) => {
    setActivePreset(preset);
    setStartDate(start);
    setEndDate(end);
    setDraftStartYear(String(start.getFullYear()));
    setDraftStartMonth(MONTHS.find((m) => m.value === start.getMonth()) || MONTHS[0]);
    setDraftEndYear(String(end.getFullYear()));
    setDraftEndMonth(MONTHS.find((m) => m.value === end.getMonth()) || MONTHS[0]);
  };

  const applyPreset = (preset) => {
    const year = today.getFullYear();
    if (preset === "this_month") {
      setRange(
        preset,
        new Date(year, today.getMonth(), 1),
        new Date(year, today.getMonth() + 1, 0),
      );
      return;
    }

    if (preset === "last_quarter") {
      const currentQuarter = Math.floor(today.getMonth() / 3);
      const lastQuarterStartMonth = currentQuarter === 0 ? 9 : (currentQuarter - 1) * 3;
      const lastQuarterYear = currentQuarter === 0 ? year - 1 : year;
      setRange(
        preset,
        new Date(lastQuarterYear, lastQuarterStartMonth, 1),
        new Date(lastQuarterYear, lastQuarterStartMonth + 3, 0),
      );
      return;
    }

    if (preset === "ytd") {
      setRange(preset, new Date(year, 0, 1), today);
      return;
    }

    if (preset === "last_year") {
      setRange(preset, new Date(year - 1, 0, 1), new Date(year - 1, 11, 31));
      return;
    }

    setActivePreset("custom");
    setCustomOpen(true);
  };

  const exportCsv = () => {
    const rows = data?.current?.time_series || [];
    if (!rows.length) {
      setToastSeverity("info");
      setToastMsg("No metrics data to export");
      setToastOpen(true);
      return;
    }

    const columns = [
      "period",
      "inquiries",
      "leads",
      "qualified_opportunities",
      "rfis_responded_to",
      "prospect_closed_won",
      "prospect_closed_lost",
      "prospects_added",
      "active_members",
      "new_members",
      "member_revenue",
      "website_visitors",
      "bre_companies",
      "jobs",
      "investment",
      "projects_won",
    ];
    const csv = [
      columns.join(","),
      ...rows.map((row) =>
        columns
          .map((key) => {
            const value = row[key] ?? "";
            return typeof value === "string" ? `"${value.replaceAll('"', '""')}"` : value;
          })
          .join(","),
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `metrics_${fmtISO(startDate)}_${fmtISO(endDate)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: PAGE_BG,
        pb: 4,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          borderBottom: "1px solid #dce4ef",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            width: "100%",
            mx: "auto",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr" },
            alignItems: "center",
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 1.75,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                background: BRAND_GREEN,
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
              }}
            >
              <BarChartIcon sx={{ fontSize: 19 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: "#00336C",
                  fontSize: 21,
                  fontWeight: 800,
                  lineHeight: 1.05,
                }}
              >
                Metrics
              </Typography>
              <Typography sx={{ color: "#3a5c08", fontSize: 12.5, mt: 0.35, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, letterSpacing: 0.3 }}>
                Performance overview • {rangeLabel}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              justifySelf: { xs: "stretch", md: "center" },
              display: "flex",
              p: 0.35,
              borderRadius: 2,
              background: "#eef2f8",
              gap: 0.35,
            }}
          >
            {[
              ["overview", "Overview"],
              ["charts", "Charts"],
              ["table", "Table"],
            ].map(([key, label]) => (
              <Button
                key={key}
                onClick={() => setActiveView(key)}
                sx={{
                  minHeight: 32,
                  px: { xs: 1.4, sm: 2.2 },
                  flex: { xs: 1, md: "0 0 auto" },
                  borderRadius: 1.5,
                  textTransform: "none",
                  color: activeView === key ? BRAND_BLUE : "#4f6383",
                  backgroundColor: activeView === key ? "#ffffff" : "transparent",
                  fontWeight: 700,
                  boxShadow:
                    activeView === key ? "0 1px 4px rgba(15, 23, 42, 0.14)" : "none",
                  "&:hover": {
                    backgroundColor: activeView === key ? "#ffffff" : "rgba(255,255,255,0.45)",
                  },
                }}
              >
                {label}
              </Button>
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "stretch", md: "flex-end" },
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Button
              onClick={() => navigate("/reports/builder")}
              startIcon={<ArticleIcon />}
              sx={{
                minHeight: 38,
                px: 1.75,
                borderRadius: 2,
                border: "1px solid #dce4ef",
                color: "#173153",
                backgroundColor: "#ffffff",
                textTransform: "none",
                fontWeight: 700,
                "&:hover": { backgroundColor: "#f7f9fc", borderColor: "#cfd9e8" },
              }}
            >
              Report Builder
            </Button>
            <Button
              onClick={() => setAiOpen(true)}
              startIcon={<AutoAwesomeIcon sx={{ color: "#648315" }} />}
              sx={{
                minHeight: 38,
                px: 1.75,
                borderRadius: 2,
                border: "1px solid #dce4ef",
                color: "#173153",
                backgroundColor: "#ffffff",
                textTransform: "none",
                fontWeight: 700,
                "&:hover": { backgroundColor: "#f7f9fc", borderColor: "#cfd9e8" },
              }}
            >
              AI Insights
            </Button>
            <Button
              onClick={exportCsv}
              startIcon={<DownloadIcon />}
              sx={{
                minHeight: 38,
                px: 1.75,
                borderRadius: 2,
                border: "1px solid #dce4ef",
                color: "#173153",
                backgroundColor: "#ffffff",
                textTransform: "none",
                fontWeight: 700,
                "&:hover": { backgroundColor: "#f7f9fc", borderColor: "#cfd9e8" },
              }}
            >
              Export
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Date Range Filters */}
      <Card
        sx={{
          width: "calc(100% - 48px)",
          mx: { xs: 2, md: 3 },
          mt: 3,
          mb: 3,
          border: "1px solid #dce4ef",
          borderRadius: 2,
          boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
          background: "#ffffff",
        }}
      >
        <CardContent sx={{ px: { xs: 2, md: 2.5 }, py: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mr: 0.5 }}>
                <CalendarMonthIcon sx={{ color: BRAND_BLUE, fontSize: 19 }} />
                <Typography sx={{ color: BRAND_BLUE, fontSize: 14, fontWeight: 700 }}>
                  Date Range
                </Typography>
              </Box>
              {[
                ["this_month", "This Month"],
                ["last_quarter", "Last Quarter"],
                ["ytd", "YTD"],
                ["last_year", "Last Year"],
                ["custom", "Custom"],
              ].map(([key, label]) => (
                <Button
                  key={key}
                  onClick={() => applyPreset(key)}
                  sx={{
                    minHeight: 34,
                    px: 1.9,
                    borderRadius: 999,
                    border: "1px solid #dce4ef",
                    textTransform: "none",
                    fontWeight: 700,
                    color: activePreset === key ? "#ffffff" : BRAND_BLUE,
                    backgroundColor: activePreset === key ? BRAND_BLUE : "#f8fafc",
                    "&:hover": {
                      backgroundColor: activePreset === key ? BRAND_BLUE : "#eef2f8",
                    },
                  }}
                >
                  {label}
                </Button>
              ))}
            </Box>
            <Button
              endIcon={<KeyboardArrowDownIcon />}
              sx={{
                ml: { md: "auto" },
                minHeight: 34,
                px: 1,
                borderRadius: 1.5,
                color: "#8a98b3",
                textTransform: "none",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              vs. previous period
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Dialog open={customOpen} onClose={() => setCustomOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: BRAND_BLUE }}>Custom Date Range</DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
              gap: 2,
              mt: 1,
            }}
          >
            <Autocomplete
              options={MONTHS}
              value={draftStartMonth}
              onChange={(_e, v) => setDraftStartMonth(v || MONTHS[0])}
              getOptionLabel={(opt) => opt.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              renderInput={(params) => <TextField {...params} label="Start Month" />}
            />
            <Autocomplete
              options={yearOptions}
              value={draftStartYear}
              onChange={(_e, v) => setDraftStartYear(v || yearOptions[0])}
              renderInput={(params) => <TextField {...params} label="Start Year" />}
            />
            <Autocomplete
              options={MONTHS}
              value={draftEndMonth}
              onChange={(_e, v) => setDraftEndMonth(v || MONTHS[0])}
              getOptionLabel={(opt) => opt.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              renderInput={(params) => <TextField {...params} label="End Month" />}
            />
            <Autocomplete
              options={yearOptions}
              value={draftEndYear}
              onChange={(_e, v) => setDraftEndYear(v || yearOptions[0])}
              renderInput={(params) => <TextField {...params} label="End Year" />}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCustomOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setActivePreset("custom");
              commitRange(draftStartMonth, draftStartYear, draftEndMonth, draftEndYear);
              setCustomOpen(false);
            }}
              sx={{
                backgroundColor: BRAND_BLUE,
                textTransform: "none",
                fontWeight: 700,
              }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <ReportPreview
        data={data}
        loading={loading}
        hasBre={hasComponent("bre.companies")}
        hasProspecting={hasComponent("prospecting.companies")}
        hasMembership={hasComponent("bre.membership")}
        dashboardLayout
        viewMode={activeView}
        rangeLabel={rangeLabel}
        onExportCsv={exportCsv}
      />

      {metricsError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {metricsError}
        </Alert>
      )}

      {/* AI Insights Modal */}
      <Dialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          ...BRAND_MODAL_PAPER_PROPS,
          sx: { ...BRAND_MODAL_PAPER_PROPS?.sx, maxHeight: "90vh" },
        }}
      >
        {/* ===== Header ===== */}
        <DialogTitle
          sx={{
            ...BRAND_MODAL_TITLE_SX,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 3,
            py: 2,
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.5,
              background: "rgba(255,255,255,0.15)",
              display: "grid",
              placeItems: "center",
              flex: "0 0 auto",
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 18, color: "#ffffff" }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 16, color: "#ffffff", lineHeight: 1.1 }}>
              AI Metrics Insights
            </Typography>
            <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.65)", mt: 0.25 }}>
              {rangeLabel}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setAiOpen(false)}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.8)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.12)", color: "#ffffff" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        {/* ===== Content ===== */}
        <DialogContent
          sx={{
            px: 0,
            py: 0,
            backgroundColor: PAGE_BG,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Action row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 3,
              pt: 2.5,
              pb: 2,
              borderBottom: aiData ? "1px solid #dce4ef" : "none",
              backgroundColor: "#ffffff",
            }}
          >
            <Button
              variant="contained"
              onClick={generateSummary}
              disabled={summaryLoading}
              startIcon={
                summaryLoading ? (
                  <CircularProgress size={15} sx={{ color: "rgba(255,255,255,0.7)" }} />
                ) : (
                  <AutoAwesomeIcon sx={{ fontSize: 17 }} />
                )
              }
              sx={{ ...PRIMARY_BUTTON_SX, borderRadius: 2, px: 2.5 }}
            >
              {summaryLoading ? "Analyzing…" : aiData && !aiData._error ? "Regenerate" : "Generate Analysis"}
            </Button>
            {!aiData && !summaryLoading && (
              <Typography sx={{ fontSize: 13, color: "#7b8aaa" }}>
                AI will analyze your metrics data and generate in-depth insights with projections.
              </Typography>
            )}
            {aiData && !aiData._error && (
              <Box sx={{ display: "flex", gap: 1, ml: 1, flexWrap: "wrap" }}>
                {aiData.sections?.length > 0 && (
                  <Chip
                    size="small"
                    label={`${aiData.sections.length} sections`}
                    sx={{ backgroundColor: `${BRAND_BLUE}14`, color: BRAND_BLUE, fontWeight: 700, fontSize: 11 }}
                  />
                )}
                {aiData.projections?.length > 0 && (
                  <Chip
                    size="small"
                    label={`${aiData.projections.length} projections`}
                    sx={{ backgroundColor: `${BRAND_GREEN}18`, color: BRAND_GREEN, fontWeight: 700, fontSize: 11 }}
                  />
                )}
                {aiData.alerts?.length > 0 && (
                  <Chip
                    size="small"
                    label={`${aiData.alerts.length} alert${aiData.alerts.length > 1 ? "s" : ""}`}
                    sx={{ backgroundColor: "#fff3e0", color: "#e65100", fontWeight: 700, fontSize: 11 }}
                  />
                )}
              </Box>
            )}
          </Box>

          {/* Error state */}
          {aiData?._error && (
            <Box sx={{ px: 3, py: 3 }}>
              <Alert severity="error">{aiData._error}</Alert>
            </Box>
          )}

          {/* Empty state */}
          {!aiData && !summaryLoading && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${BRAND_BLUE}14 0%, ${BRAND_GREEN}14 100%)`,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: 28, color: BRAND_BLUE }} />
              </Box>
              <Typography sx={{ fontWeight: 700, color: "#344863", fontSize: 15 }}>
                No analysis yet
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#7b8aaa", textAlign: "center", maxWidth: 360 }}>
                Click "Generate Analysis" to get AI-powered insights, trend analysis, and 3-month projections.
              </Typography>
            </Box>
          )}

          {/* Tabs + content */}
          {aiData && !aiData._error && (
            <>
              <Tabs
                value={aiTab}
                onChange={(_e, v) => setAiTab(v)}
                sx={{
                  px: 3,
                  backgroundColor: "#ffffff",
                  borderBottom: "1px solid #dce4ef",
                  minHeight: 44,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 13,
                    minHeight: 44,
                    color: "#4f6383",
                    px: 2,
                  },
                  "& .Mui-selected": { color: BRAND_BLUE },
                  "& .MuiTabs-indicator": {
                    backgroundColor: BRAND_BLUE,
                    height: 3,
                    borderRadius: "3px 3px 0 0",
                  },
                }}
              >
                <Tab label="Analysis" value={0} />
                <Tab label="Projections" value={1} />
                <Tab label="Highlights" value={2} />
              </Tabs>

              <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>

                {/* ── TAB 0: Analysis ── */}
                {aiTab === 0 && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      minWidth: 0,
                    }}
                  >
                    {aiData.sections?.map((section, i) => (
                      <Box
                        key={i}
                        sx={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #dce4ef",
                          borderRadius: 2.5,
                          p: 2.5,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          boxShadow: "0 2px 8px rgba(15,23,42,0.05)",
                        }}
                      >
                        {/* Section header */}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {section.trend === "up" && (
                              <TrendingUpIcon sx={{ fontSize: 18, color: BRAND_GREEN }} />
                            )}
                            {section.trend === "down" && (
                              <TrendingDownIcon sx={{ fontSize: 18, color: "#d32f2f" }} />
                            )}
                            {section.trend === "neutral" && (
                              <TrendingFlatIcon sx={{ fontSize: 18, color: "#7b8aaa" }} />
                            )}
                            <Typography sx={{ fontWeight: 800, fontSize: 13.5, color: BRAND_BLUE }}>
                              {section.title}
                            </Typography>
                          </Box>
                          {section.key_stat && (
                            <Chip
                              label={section.key_stat}
                              size="small"
                              sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                backgroundColor:
                                  section.trend === "up"
                                    ? `${BRAND_GREEN}18`
                                    : section.trend === "down"
                                    ? "#ffebee"
                                    : "#f0f4f8",
                                color:
                                  section.trend === "up"
                                    ? BRAND_GREEN
                                    : section.trend === "down"
                                    ? "#c62828"
                                    : "#4f6383",
                              }}
                            />
                          )}
                        </Box>
                        {/* Summary text */}
                        <Typography
                          sx={{
                            fontSize: 13,
                            lineHeight: 1.75,
                            color: "#344863",
                          }}
                        >
                          {section.summary}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* ── TAB 1: Projections ── */}
                {aiTab === 1 && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    {aiData.projections?.map((proj, i) => (
                      <Box
                        key={i}
                        sx={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #dce4ef",
                          borderRadius: 2.5,
                          p: 2.5,
                          boxShadow: "0 2px 8px rgba(15,23,42,0.05)",
                          minWidth: 0,
                        }}
                      >
                        {/* Chart header */}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                          <Typography sx={{ fontWeight: 800, fontSize: 13.5, color: BRAND_BLUE }}>
                            {proj.display_name}
                          </Typography>
                          <Chip
                            label={`${proj.confidence} confidence`}
                            size="small"
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              backgroundColor:
                                proj.confidence === "high"
                                  ? `${BRAND_GREEN}18`
                                  : proj.confidence === "low"
                                  ? "#fff3e0"
                                  : "#f0f4f8",
                              color:
                                proj.confidence === "high"
                                  ? BRAND_GREEN
                                  : proj.confidence === "low"
                                  ? "#e65100"
                                  : "#4f6383",
                            }}
                          />
                        </Box>

                        {/* Chart */}
                        <Box sx={{ width: "100%", minWidth: 0, height: 160, minHeight: 160 }}>
                          <ResponsiveContainer
                            width="100%"
                            height="100%"
                            minWidth={0}
                            initialDimension={{ width: 360, height: 160 }}
                          >
                            <LineChart
                              data={proj.chart_data}
                              margin={{ top: 4, right: 8, bottom: 0, left: 0 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                              <XAxis
                                dataKey="period"
                                tick={{ fontSize: 10, fill: "#7b8aaa" }}
                                tickFormatter={fmtPeriod}
                                interval="preserveStartEnd"
                              />
                              <YAxis
                                tick={{ fontSize: 10, fill: "#7b8aaa" }}
                                tickFormatter={(v) => fmtProjectionValue(proj.metric, v)}
                                width={52}
                              />
                              <ReTooltip
                                formatter={(value, name) => [
                                  fmtProjectionValue(proj.metric, value),
                                  name === "actual" ? "Actual" : "Projected",
                                ]}
                                labelFormatter={fmtPeriod}
                                contentStyle={{
                                  fontSize: 12,
                                  borderRadius: 8,
                                  border: "1px solid #dce4ef",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                }}
                              />
                              {/* Vertical divider at last actual period */}
                              {proj.chart_data?.filter((d) => d.actual != null).slice(-1)[0] && (
                                <ReferenceLine
                                  x={proj.chart_data.filter((d) => d.actual != null).slice(-1)[0].period}
                                  stroke="#dce4ef"
                                  strokeDasharray="4 2"
                                  label={{ value: "Today", position: "insideTopRight", fontSize: 9, fill: "#7b8aaa" }}
                                />
                              )}
                              <Line
                                type="monotone"
                                dataKey="actual"
                                stroke={BRAND_BLUE}
                                strokeWidth={2}
                                dot={{ r: 2.5, fill: BRAND_BLUE, strokeWidth: 0 }}
                                connectNulls={false}
                                name="actual"
                              />
                              <Line
                                type="monotone"
                                dataKey="projected"
                                stroke={BRAND_GREEN}
                                strokeWidth={2}
                                strokeDasharray="6 3"
                                dot={{ r: 2.5, fill: BRAND_GREEN, strokeWidth: 0 }}
                                connectNulls={false}
                                name="projected"
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </Box>

                        {/* Legend */}
                        <Box sx={{ display: "flex", gap: 2, mt: 1, mb: 0.5 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Box sx={{ width: 14, height: 2, backgroundColor: BRAND_BLUE, borderRadius: 1 }} />
                            <Typography sx={{ fontSize: 10, color: "#7b8aaa", fontWeight: 600 }}>Actual</Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Box sx={{ width: 14, height: 2, backgroundColor: BRAND_GREEN, borderRadius: 1, borderTop: `2px dashed ${BRAND_GREEN}`, background: "none" }} />
                            <Typography sx={{ fontSize: 10, color: "#7b8aaa", fontWeight: 600 }}>Projected</Typography>
                          </Box>
                        </Box>

                        {/* AI reasoning */}
                        {proj.reasoning && (
                          <Typography
                            sx={{
                              fontSize: 11.5,
                              color: "#5b6f91",
                              lineHeight: 1.6,
                              mt: 1,
                              pt: 1,
                              borderTop: "1px solid #f0f4f8",
                              fontStyle: "italic",
                            }}
                          >
                            {proj.reasoning}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}

                {/* ── TAB 2: Highlights ── */}
                {aiTab === 2 && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {/* Highlight cards */}
                    {aiData.highlights?.length > 0 && (
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                          <EmojiEventsIcon sx={{ fontSize: 17, color: BRAND_GREEN }} />
                          <Typography sx={{ fontWeight: 800, fontSize: 13.5, color: BRAND_BLUE }}>
                            Key Highlights
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                            gap: 1.5,
                          }}
                        >
                          {aiData.highlights.map((h, i) => (
                            <Box
                              key={i}
                              sx={{
                                backgroundColor: "#ffffff",
                                border: "1px solid #dce4ef",
                                borderTop: `3px solid ${BRAND_GREEN}`,
                                borderRadius: 2,
                                p: 2,
                                boxShadow: "0 2px 6px rgba(15,23,42,0.04)",
                              }}
                            >
                              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#7b8aaa", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}>
                                {h.label}
                              </Typography>
                              <Typography sx={{ fontSize: 15, fontWeight: 800, color: BRAND_BLUE, lineHeight: 1.2, mb: 0.5 }}>
                                {h.value}
                              </Typography>
                              {h.context && (
                                <Typography sx={{ fontSize: 12, color: "#5b6f91", lineHeight: 1.5 }}>
                                  {h.context}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Alerts */}
                    {aiData.alerts?.length > 0 && (
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                          <WarningAmberIcon sx={{ fontSize: 17, color: "#e65100" }} />
                          <Typography sx={{ fontWeight: 800, fontSize: 13.5, color: BRAND_BLUE }}>
                            Alerts & Concerns
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          {aiData.alerts.map((a, i) => (
                            <Box
                              key={i}
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1.5,
                                backgroundColor: a.severity === "critical" ? "#fff8f6" : "#fffbf2",
                                border: `1px solid ${a.severity === "critical" ? "#ffccbc" : "#ffe0b2"}`,
                                borderLeft: `4px solid ${a.severity === "critical" ? "#bf360c" : "#e65100"}`,
                                borderRadius: 2,
                                p: 2,
                              }}
                            >
                              <WarningAmberIcon
                                sx={{
                                  fontSize: 17,
                                  color: a.severity === "critical" ? "#bf360c" : "#e65100",
                                  mt: 0.2,
                                  flex: "0 0 auto",
                                }}
                              />
                              <Box>
                                <Chip
                                  label={a.severity.toUpperCase()}
                                  size="small"
                                  sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    mb: 0.5,
                                    backgroundColor: a.severity === "critical" ? "#ffccbc" : "#ffe0b2",
                                    color: a.severity === "critical" ? "#bf360c" : "#e65100",
                                  }}
                                />
                                <Typography sx={{ fontSize: 13, color: "#344863", lineHeight: 1.6 }}>
                                  {a.message}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {!aiData.highlights?.length && !aiData.alerts?.length && (
                      <Typography sx={{ fontSize: 13, color: "#7b8aaa", textAlign: "center", py: 4 }}>
                        No highlights or alerts available.
                      </Typography>
                    )}
                  </Box>
                )}

              </Box>
            </>
          )}
        </DialogContent>

        {/* ===== Footer ===== */}
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            borderTop: "1px solid #dce4ef",
            backgroundColor: "#ffffff",
          }}
        >
          <Button
            onClick={copySummary}
            disabled={!aiData || !!aiData._error}
            startIcon={<ContentCopyIcon />}
            sx={{ ...SECONDARY_BUTTON_SX, borderRadius: 2 }}
          >
            Copy
          </Button>
          <Button
            onClick={downloadSummary}
            disabled={!aiData || !!aiData._error}
            startIcon={<DownloadIcon />}
            sx={{ ...PRIMARY_BUTTON_SX, borderRadius: 2, ml: "auto" }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
      >
        <Alert severity={toastSeverity}>{toastMsg}</Alert>
      </Snackbar>
    </Box>
  );
}
